import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { usePageSEO } from '@/utils/seo';
import {
  ORGANIZATION_SCHEMA, FOUNDER_SCHEMA, WEBSITE_SCHEMA,
  makeBreadcrumb,
} from '@/utils/schema';
import { POSTS } from '@/data/blogPosts';
import { ArrowRight, Calendar } from '@phosphor-icons/react';

export default function BlogIndexPage() {
  usePageSEO({
    title: 'PayGBot Blog — Real Talk About Building A Business In Real Life',
    description:
      'Founder essays from Vickie Mcglawn on building real businesses during real life — for busy parents, beginners, side hustlers, and anyone tired of courses that overwhelm instead of help.',
    path: '/blog',
    keywords:
      'PayGBot blog, online business for busy people, building a business after illness, simple business systems, AI business operating system blog',
    jsonLd: [
      ORGANIZATION_SCHEMA,
      FOUNDER_SCHEMA,
      WEBSITE_SCHEMA,
      makeBreadcrumb([
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        '@id': 'https://paygbot.com/blog#blog',
        name: 'PayGBot Blog',
        description:
          'Founder essays on building real businesses during real life. Written by Vickie Mcglawn.',
        url: 'https://paygbot.com/blog',
        publisher: { '@id': 'https://paygbot.com/#organization' },
        author: { '@id': 'https://paygbot.com/#founder' },
        blogPost: POSTS.map((p) => ({
          '@type': 'BlogPosting',
          headline: p.title,
          description: p.description,
          url: `https://paygbot.com/blog/${p.slug}`,
          datePublished: p.publishedDate,
          author: { '@id': 'https://paygbot.com/#founder' },
        })),
      },
    ],
  });

  return (
    <MarketingLayout>
      <section className="px-4 sm:px-6 lg:px-8 pt-16 pb-10 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <p className="text-xs uppercase tracking-widest text-blue-600 font-semibold">PayGBot Blog</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
            Real Talk About Building A Business In Real Life
          </h1>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            Essays from Vickie Mcglawn — built for busy parents, beginners, side hustlers, and anyone who is tired of courses that promise everything and produce nothing.
          </p>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 pb-20 bg-white">
        <div className="max-w-3xl mx-auto space-y-5" data-testid="blog-post-list">
          {POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="block bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md rounded-xl p-6 transition-all"
            >
              <div className="flex items-center text-xs text-slate-500 gap-2 mb-2">
                <Calendar size={14} weight="regular" />
                <time dateTime={p.publishedDate}>
                  {new Date(p.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
                <span>·</span>
                <span>Vickie Mcglawn</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-2 leading-snug">{p.title}</h2>
              <p className="text-slate-600 leading-relaxed">{p.excerpt}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-blue-600 font-medium text-sm">
                Read essay <ArrowRight size={14} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
